import React, { useEffect, useState, useCallback } from 'react';
import { View, Text, StyleSheet, FlatList, ActivityIndicator } from 'react-native';

import { api } from '../../src/api/client';
import { AdminToolbar } from '../../src/components/AdminList';
import ContentContainer from '../../src/components/ContentContainer';
import PageFooter from '../../src/components/PageFooter';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';

interface AuditEntry {
  readonly id: string;
  readonly userId: string | null;
  readonly action: string;
  readonly resource: string | null;
  readonly details: string | null;
  readonly ipAddress: string | null;
  readonly createdAt: string;
}

/** Page size for audit log pagination */
const PAGE_LIMIT = 50;

/** Action filter options for the audit log toolbar */
const ACTION_FILTERS = [
  { label: 'Login', value: 'login' },
  { label: 'Failed', value: 'login_failed' },
  { label: 'Register', value: 'register' },
  { label: 'Location', value: 'location_create' },
  { label: 'Bay', value: 'bay_create' },
  { label: 'Admin Edit', value: 'admin_update' },
];

/** AdminAuditLog - displays paginated, searchable security and activity audit events */
export default function AdminAuditLog() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const fetchLog = useCallback(async (p = 1, s = '', action = '', signal?: AbortSignal) => {
    setLoading(true);
    try {
      /* Fetch paginated audit entries with optional search/action filters */
      const params = new URLSearchParams({ page: String(p), limit: String(PAGE_LIMIT) });
      if (s) params.set('search', s);
      if (action) params.set('action', action);
      const data = await api<{ entries: AuditEntry[]; total: number }>(`/api/admin/audit-log?${params}`);
      if (signal?.aborted) return;
      setEntries(data.entries);
      setTotal(data.total);
      setPage(p);
    } catch (err) { if (!(signal?.aborted)) console.error(err); }
    finally { if (!(signal?.aborted)) setLoading(false); }
  }, []);

  useEffect(() => { const ac = new AbortController(); fetchLog(1, '', '', ac.signal); return () => ac.abort(); }, []);

  const handleSearch = () => fetchLog(1, search, actionFilter);
  const handleFilter = (v: string) => { setActionFilter(v); fetchLog(1, search, v); };
  const handlePage = (p: number) => fetchLog(p, search, actionFilter);

  /** Maps audit action to a severity color */
  const actionColor = (a: string) => {
    if (a.includes('failed') || a.includes('locked')) return '#dc2626';
    if (a === 'login' || a === 'register') return '#059669';
    return '#2563eb';
  };

  /** Parses JSON details string into readable key-value pairs */
  const formatDetails = (d: string | null) => {
    if (!d) return '';
    try {
      const obj = JSON.parse(d);
      return Object.entries(obj).map(([k, v]) => `${k}: ${v}`).join(', ');
    } catch { /* non-critical: details may be plain text */ return d; }
  };

  const csvEndpoint = `/api/admin/audit-log?${new URLSearchParams({ ...(search && { search }), ...(actionFilter && { action: actionFilter }) })}`;

  const renderItem = ({ item }: { item: AuditEntry }) => (
    <View style={styles.row}>
      <View style={[styles.dot, { backgroundColor: actionColor(item.action) }]} />
      <View style={styles.rowBody}>
        <View style={styles.rowHeader}>
          <Text style={[styles.action, { color: actionColor(item.action) }]}>{item.action}</Text>
          {item.resource ? <Text style={styles.resource}>{item.resource}</Text> : null}
          <Text style={styles.time}>{new Date(item.createdAt).toLocaleString()}</Text>
        </View>
        <Text style={styles.detail} numberOfLines={1}>
          {item.ipAddress || '—'}{formatDetails(item.details) ? ` · ${formatDetails(item.details)}` : ''}
        </Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <ContentContainer role="admin" footer={false}>
      <AdminToolbar
        search={search} onSearchChange={setSearch} onSearch={handleSearch}
        total={total} page={page} limit={50} onPageChange={handlePage}
        csvEndpoint={csvEndpoint} csvFilename="audit-log.csv" label="event"
        filterOptions={ACTION_FILTERS} activeFilter={actionFilter} onFilterChange={handleFilter}
      />
      {loading ? (
        <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
      ) : (
        <FlatList ListFooterComponent={<PageFooter />}
          data={entries}
          keyExtractor={item => item.id}
          renderItem={renderItem}
          ListEmptyComponent={<Text style={styles.empty}>No events found</Text>}
        />
      )}
      </ContentContainer>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background, padding: spacing.md },
  row: { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  dot: { width: 8, height: 8, borderRadius: 4, marginTop: 6, marginRight: 10 },
  rowBody: { flex: 1 },
  rowHeader: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8 },
  action: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold },
  resource: { fontSize: fontSize.xs, color: c.textSecondary, backgroundColor: c.surfaceSecondary, paddingHorizontal: 6, paddingVertical: 1, borderRadius: 4, overflow: 'hidden' },
  time: { fontSize: fontSize.xs, color: c.textLight, marginLeft: 'auto' },
  detail: { fontSize: fontSize.xs, color: c.textLight, marginTop: 2 },
  empty: { textAlign: 'center', color: c.textSecondary, marginTop: spacing.xl, fontSize: fontSize.md },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
